import TelemetryDeck from '@telemetrydeck/sdk';

(function () {
    const APP_ID = '20829BB6-A87D-4198-A10C-6DF80FFD9FD9';
    const INTERACTION_CLIENT_USER = 'codex-interactions-v1';
    const PAGE_VIEW_SDK_URL = 'https://cdn.telemetrydeck.com/websdk/telemetrydeck.min.js';
    const STORAGE_KEY = 'codexTelemetrySession';
    const PATREON_HOSTS = ['patreon.com', 'www.patreon.com'];

    const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 120);

    const randomID = () => {
        if (window.crypto && window.crypto.randomUUID) {
            return window.crypto.randomUUID();
        }

        return `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    };

    const getSessionID = () => {
        try {
            let sessionID = window.sessionStorage.getItem(STORAGE_KEY);

            if (!sessionID) {
                sessionID = randomID();
                window.sessionStorage.setItem(STORAGE_KEY, sessionID);
            }

            return sessionID;
        } catch (error) {
            return randomID();
        }
    };

    const isPrivateIPv4 = (host) => /^(10\.|192\.168\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(host);

    const isTestMode = () => {
        const host = window.location.hostname;

        return window.location.protocol === 'file:' ||
            host === 'localhost' ||
            host === '127.0.0.1' ||
            host === '::1' ||
            host === '[::1]' ||
            isPrivateIPv4(host);
    };

    const hasRequiredBrowserAPIs = () => Boolean(
        window.fetch &&
        window.crypto &&
        window.crypto.subtle &&
        window.TextEncoder
    );

    const loadPageViewSDK = () => {
        if (document.querySelector('[data-codex-telemetry-pageviews]')) {
            return;
        }

        const script = document.createElement('script');

        script.src = PAGE_VIEW_SDK_URL;
        script.dataset.appId = APP_ID;
        script.dataset.codexTelemetryPageviews = '';
        document.head.appendChild(script);
    };

    const whenFirstVisible = (callback) => {
        if (!document.hidden) {
            callback();
            return;
        }

        const handleVisibilityChange = () => {
            if (document.hidden) {
                return;
            }

            document.removeEventListener('visibilitychange', handleVisibilityChange);
            callback();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
    };

    let telemetry = null;

    const getPageKind = () => {
        const path = window.location.pathname;
        const body = document.body;

        if (path === '/' || body.classList.contains('home-template')) {
            return 'home';
        }

        if (path === '/index/') {
            return 'archive';
        }

        if (body.classList.contains('post-template')) {
            return 'post';
        }

        if (body.classList.contains('tag-template')) {
            return 'tag';
        }

        if (body.classList.contains('author-template')) {
            return 'author';
        }

        if (body.classList.contains('page-template')) {
            return 'page';
        }

        if (body.classList.contains('error-template')) {
            return 'error';
        }

        return 'other';
    };

    const getMemberState = () => document.body.dataset.memberState || 'unknown';

    const getSurface = (node) => {
        if (!node) {
            return 'unknown';
        }

        if (node.closest('.site-drawer')) {
            return 'drawer';
        }

        if (node.closest('.site-header')) {
            return 'header';
        }

        if (node.closest('.site-footer')) {
            return 'footer';
        }

        if (node.closest('.paywall-block')) {
            return 'paywall';
        }

        if (node.closest('.home-intro')) {
            return 'home_intro';
        }

        if (node.closest('.section-tail')) {
            return 'section_tail';
        }

        if (node.closest('.weekly-section')) {
            return 'weekly_section';
        }

        if (node.closest('.brief-rail')) {
            return 'brief_rail';
        }

        if (node.closest('.content-stream')) {
            return 'content_stream';
        }

        if (node.closest('.gh-content')) {
            return 'post_content';
        }

        return 'unknown';
    };

    const getCommonPayload = () => ({
        'Page.kind': getPageKind(),
        'Page.path': window.location.pathname,
        'Member.state': getMemberState()
    });

    const signal = (type, payload = {}) => {
        telemetry.signal(type, {
            ...getCommonPayload(),
            ...payload
        }).catch(() => {});
    };

    const getMembershipTarget = (url) => {
        if (url.pathname.includes('/settings/memberships')) {
            return 'manage_subscription';
        }

        return 'membership';
    };

    const getPortalAction = (node) => node.dataset.portal || (node.getAttribute('href') || '').replace('#/portal/', '') || 'unknown';

    const getArchiveKind = (link) => {
        const label = cleanText(link.textContent);
        const path = new URL(link.href, window.location.href).pathname;

        if (label.includes('短篇')) {
            return 'briefs';
        }

        if (label.includes('週報') || path.includes('/tag/news/')) {
            return 'weekly';
        }

        if (path === '/index/') {
            return 'articles';
        }

        return 'unknown';
    };

    const trackInitialState = () => {
        if (document.querySelector('#paywall')) {
            signal('Post.paywallViewed');
        }
    };

    const trackClick = (event) => {
        const target = event.target.closest('a, button');

        if (!target) {
            return;
        }

        if (target.matches('[data-nav-toggle]')) {
            signal('Navigation.drawerOpened', {
                'Action.surface': getSurface(target)
            });
        }

        if (target.matches('[data-ghost-search]')) {
            signal('Search.opened', {
                'Action.surface': getSurface(target)
            });
        }

        if (target.matches('.code-copy')) {
            signal('Code.copyClicked', {
                'Action.surface': getSurface(target)
            });
        }

        const portalLink = target.closest('[data-portal]');

        if (portalLink) {
            signal('Portal.opened', {
                'Action.action': getPortalAction(portalLink),
                'Action.surface': getSurface(portalLink)
            });
        }

        const link = target.closest('a[href]');

        if (!link) {
            return;
        }

        let url = null;

        try {
            url = new URL(link.href, window.location.href);
        } catch (error) {
            return;
        }

        if (PATREON_HOSTS.includes(url.hostname)) {
            signal('Membership.ctaClicked', {
                'Action.label': cleanText(link.textContent),
                'Action.surface': getSurface(link),
                'Action.target': getMembershipTarget(url)
            });
        }

        if (link.closest('.section-tail')) {
            signal('Content.archiveClicked', {
                'Action.label': cleanText(link.textContent),
                'Action.surface': getSurface(link),
                'Content.kind': getArchiveKind(link)
            });
        }
    };

    const startTelemetry = () => {
        loadPageViewSDK();

        if (!hasRequiredBrowserAPIs()) {
            return;
        }

        const sessionID = getSessionID();

        // SDK 2.0.4 requires a non-empty clientUser. A single synthetic identity
        // keeps interaction events out of visitor metrics while sessionID retains
        // per-tab event sequencing.
        telemetry = new TelemetryDeck({
            appID: APP_ID,
            clientUser: INTERACTION_CLIENT_USER,
            sessionID,
            testMode: isTestMode()
        });

        trackInitialState();
        document.addEventListener('click', trackClick, true);
    };

    const startWhenReady = () => whenFirstVisible(startTelemetry);

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startWhenReady, {once: true});
    } else {
        startWhenReady();
    }
}());
